import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, ArrowUpRight, Star } from 'lucide-react';

const PHONE = '(505) 589-3461';
const PHONE_HREF = 'tel:+15055893461';
const EMAIL = 'opsceo@msdpaintingservices.org';

export const Footer = () => {
    const navigate = useNavigate();
    return (
        <footer
            id="contact"
            data-testid="msd-footer"
            className="relative z-20 bg-ink-950 px-6 md:px-10 pt-32 md:pt-48 pb-12 border-t border-white/5 overflow-x-clip"
        >
            <div className="mx-auto max-w-[1600px]">
                {/* Veteran ribbon */}
                <div className="mb-10 inline-flex items-center gap-3 border border-adobe-500/30 rounded-full px-4 py-2 bg-adobe-500/5">
                    <Star size={12} className="text-adobe-500" strokeWidth={1.5} />
                    <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-bone-100">
                        Veteran Owned & Operated · Serving all of New Mexico
                    </span>
                </div>

                <div className="flex items-center gap-4 mb-10">
                    <span className="w-10 h-px bg-adobe-500" />
                    <span className="eyebrow">Chapter VI · Contact</span>
                </div>

                <motion.h2
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 1.2, ease: [0.7, 0, 0.2, 1] }}
                    className="font-display font-black uppercase tracking-[-0.035em] leading-[0.85] text-bone-100"
                    style={{ fontSize: 'clamp(4rem, 15vw, 15rem)' }}
                >
                    Let's <span className="italic font-light text-adobe-500">talk.</span>
                </motion.h2>

                <div className="mt-16 md:mt-24 grid grid-cols-12 gap-10 md:gap-16">
                    <div className="col-span-12 md:col-span-6 min-w-0">
                        <div className="font-body text-lg md:text-2xl font-light text-bone-200 leading-snug max-w-lg">
                            Free, no-obligation estimates for interiors, exteriors, cabinets, stucco and commercial work — anywhere in New Mexico.
                        </div>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <button
                                type="button"
                                onClick={() => navigate('/estimate')}
                                data-testid="footer-cta-call"
                                className="btn-pill btn-pill--solid"
                            >
                                <Phone size={14} strokeWidth={1.5} />
                                Get a free estimate
                            </button>
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="footer-cta-email"
                                className="btn-pill btn-pill--ghost max-w-full break-all text-[11px] sm:text-[13px]"
                            >
                                <Mail size={14} strokeWidth={1.5} />
                                {EMAIL}
                            </a>
                        </div>
                    </div>

                    <div className="col-span-12 md:col-span-6 grid grid-cols-2 gap-6 md:gap-10 min-w-0">
                        <div>
                            <div className="eyebrow mb-3">Studio</div>
                            <div className="font-body text-sm text-bone-200 leading-relaxed flex items-start gap-2">
                                <MapPin size={12} strokeWidth={1.5} className="text-adobe-500 mt-1 shrink-0" />
                                <div>
                                    MSD Painting Services<br />
                                    1745 Blueberry Dr NE<br />
                                    Rio Rancho, NM 87144<br />
                                    <span className="text-bone-400">By appointment</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="eyebrow mb-3">Service Area</div>
                            <ul className="font-body text-sm text-bone-200 space-y-1.5">
                                <li>All of New Mexico</li>
                                <li className="text-bone-400">— Rio Rancho</li>
                                <li className="text-bone-400">— Albuquerque</li>
                                <li className="text-bone-400">— Santa Fe</li>
                                <li className="text-bone-400">— Las Cruces</li>
                            </ul>
                        </div>
                        <div>
                            <div className="eyebrow mb-3">Hours</div>
                            <div className="font-body text-sm text-bone-200 space-y-1">
                                <div>Mon — Fri · 7:00–18:00</div>
                                <div>Sat · By appointment</div>
                                <div className="text-bone-400">Commercial · after-hours</div>
                            </div>
                        </div>
                        <div>
                            <div className="eyebrow mb-3">Follow</div>
                            <ul className="space-y-2">
                                <li>
                                    <a href="#" className="link-underline font-body text-sm text-bone-200 inline-flex items-center gap-1.5">
                                        Instagram <ArrowUpRight size={12} />
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="https://www.google.com/search?sca_esv=dd11972937bd8ee9&q=MSD+Painting+Services+Reviews"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="footer-google-reviews"
                                        className="link-underline font-body text-sm text-bone-200 inline-flex items-center gap-1.5"
                                    >
                                        Google Reviews <ArrowUpRight size={12} />
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Colossal outline monogram */}
                <div className="mt-24 md:mt-32 relative overflow-hidden">
                    <div className="font-display font-black leading-[0.8] tracking-[-0.04em] text-outline whitespace-nowrap" style={{ fontSize: 'clamp(7rem, 22vw, 22rem)' }}>
                        MSD PAINTING
                    </div>
                </div>

                <div className="mt-10 flex flex-wrap items-center justify-between gap-4 pt-8 border-t border-white/10">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-bone-500">
                        © {new Date().getFullYear()} MSD Painting Services · Rio Rancho · NM
                    </div>
                    <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <li>
                            <Link
                                to="/terms"
                                data-testid="footer-terms-link"
                                className="link-underline font-mono text-[10px] tracking-[0.28em] uppercase text-bone-400 hover:text-bone-100"
                            >
                                Terms & Conditions
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/privacy"
                                data-testid="footer-privacy-link"
                                className="link-underline font-mono text-[10px] tracking-[0.28em] uppercase text-bone-400 hover:text-bone-100"
                            >
                                Privacy Policy
                            </Link>
                        </li>
                    </ul>
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-bone-500 flex items-center gap-2">
                        <Star size={10} className="text-adobe-500" strokeWidth={1.5} />
                        Veteran Owned · Licensed · Bonded · Insured
                    </div>
                </div>
            </div>
        </footer>
    );
};
